/**
 * @fileoverview StreamingRecorder component
 * @description A component that handles audio recording UI
 * @example
 * // Usage in StreamingRecorder component:
 * import { StreamingRecorder } from './StreamingRecorder'
 *
 * function Component() {
 *   // ...
 * }
 */

import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useActivityPing } from '../../hooks/useActivityPing'
import { useAudioStreamer } from '../../hooks/useAudioStreamer'
import { ModalContext } from '../../ModalContext'
import { PlayerButton } from '../base/PlayerButton'
import { ProcessingButton } from '../base/ProcessingButton'
import { ProgressButton } from '../base/ProgressButton'
import { RecordingButton } from '../base/RecordingButton'

import { NavigationButton } from '../NavigationButton'
import { GenericModalContent } from '../questions/GenericModalContent'
import { PromptModalContent } from '../questions/PromptModalContent'
import css from './StreamingRecorder.module.css'

type Titles = Record<
    'loading' | 'processing' | 'play' | 'playing' | 'record' | 'recording',
    string | undefined
>

/**
 * @interface StreamingRecorderProps
 * @description Props for the StreamingRecorder component
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */
interface StreamingRecorderProps extends React.HTMLAttributes<HTMLDivElement> {
    titles?: Partial<Titles>
    skippable?: boolean
    attempt: number
    audio: Novoic.Audio | Novoic.Audio[]
    recordingStartMode: Novoic.RecordingMode
    recordingStopMode: Novoic.RecordingMode
    maxRecordingTime?: number
    onExit: () => Promise<void>
    onStateChange: (state: StreamingState) => void
    onStreamingEnded: (recordingUUID: string) => void
}

/**
 * @enum {string} StreamingState
 * @description The state of the streaming recorder
 * @property {string} IDLE - The recorder is idle
 * @property {string} LOADING - The recorder is loading
 * @property {string} PLAYING - The recorder is playing
 * @property {string} STREAMING - The recorder is streaming
 * @property {string} PROCESSING - The recorder is processing
 */
export enum StreamingState {
    IDLE = 'IDLE',
    LOADING = 'LOADING',
    PLAYING = 'PLAYING',
    STREAMING = 'STREAMING',
    PROCESSING = 'PROCESSING',
}

/**
 * @enum {string} RecordingMode
 * @description The mode of recording
 * @property {string} AUTO - The recording mode is automatic
 * @property {string} MANUAL - The recording mode is manual
 */
enum RecordingMode {
    AUTO = 'auto',
    MANUAL = 'manual',
}

/**
 * @function StreamingRecorder
 * @description Component for recording audio with streaming capabilities
 * @param {StreamingRecorderProps} props - The props for the StreamingRecorder component
 * @returns {JSX.Element} The StreamingRecorder component
 */
export function StreamingRecorder(props: StreamingRecorderProps) {
    const {
        attempt,
        audio,
        titles: givenTitles,
        skippable,
        recordingStartMode,
        recordingStopMode,
        maxRecordingTime,
        onExit,
        onStreamingEnded,
        onStateChange,
    } = props
    const [state, setState] = useState<StreamingState>(StreamingState.LOADING)
    const [player, recorder, isConnected, error] = useAudioStreamer(attempt)
    const [recordingUUID, setRecordingUUID] = useState<string>()
    const ping = useActivityPing()
    const modal = useContext(ModalContext)
    const video = useRef(document.createElement('video'))
    const { t } = useTranslation()

    const DEFAULT_TITLES = {
        loading: t('streaming.loading'),
        play: t('streaming.play'),
        playing: t('streaming.playing'),
        record: t('streaming.record'),
        recording: t('streaming.recordingManual'),
    }

    let file: string
    let duration: number

    if (Array.isArray(audio)) {
        file = t(audio[attempt].file)
        duration = Number(t(`${audio[attempt].duration}`))
    } else {
        file = t(audio.file)
        duration = parseInt(t(`${audio.duration}`))
    }

    const isIdle = state === StreamingState.IDLE
    const isLoading = state === StreamingState.LOADING
    const isPlaying = state === StreamingState.PLAYING
    const isStreaming = state === StreamingState.STREAMING
    const isProcessing = state === StreamingState.PROCESSING

    const autoStart = recordingStartMode === RecordingMode.AUTO

    const titles = Object.assign({}, DEFAULT_TITLES, givenTitles)

    useEffect(() => {
        video.current.setAttribute('title', `${Date.now()}`)
        video.current.setAttribute('alt', 'Question')
        video.current.setAttribute('playsinline', 'true')
        video.current.style.position = 'fixed'
        video.current.style.top = '0px'
        video.current.width = 1
        video.current.height = 1

        document.body.appendChild(video.current)
    }, [])

    useEffect(() => {
        if (maxRecordingTime - recorder.currentTime <= 0) {
            onRecordingEnd()
        }
    }, [recorder.currentTime])

    /**
     * Reset the state if there is a new attempt
     */
    useEffect(() => {
        setState(StreamingState.LOADING)
    }, [attempt])

    /**
     * When the state changes, propagate the updated state to the parent
     */
    useEffect(() => {
        onStateChange(state)
    }, [state])

    /**
     * Start recording when the track is ended
     * and recording start mode is set to auto
     */
    useEffect(() => {
        if (player.track.currentTime > 0) {
            startRecording()
        }
    }, [player.track.isEnded])

    useEffect(() => {
        console.log('is connected: ', isConnected)
        if (isConnected) {
            setState(StreamingState.IDLE)
        }
    }, [isConnected])

    useEffect(() => {
        if (error) {
            modal.open({
                render: ({ onConfirm, onCancel }) => (
                    <GenericModalContent
                        caption={t('modals.genericError.caption')}
                        text={t('modals.genericError.text')}
                        confirmText={t('modals.genericError.buttons.confirm')}
                        cancelText={t('modals.genericError.buttons.cancel')}
                        onCancel={async () => {
                            await onExit()
                            await onCancel()
                        }}
                        onConfirm={async () => {
                            window.location.reload()
                            await onConfirm()
                        }}
                    />
                ),
                canClose: false,
            })
        }
    }, [error])

    async function startRecording() {
        if (player.track.isEnded && autoStart) {
            try {
                const recordingUUID = await recorder.start()
                setRecordingUUID(recordingUUID)
                setState(StreamingState.STREAMING)
            } catch (e) {
                console.error(e)
            }
        }
    }

    async function onRecordingEnd() {
        setState(StreamingState.PROCESSING)
        await recorder.stop()
        onStreamingEnded(recordingUUID)
    }

    async function playStream() {
        setState(StreamingState.PLAYING)

        const stream = await player.play(file)
        video.current.srcObject = stream
        await video.current.play()
        await ping()
    }

    async function skipStream() {
        await player.skip()
    }

    let button: JSX.Element

    if (isIdle) {
        button = <PlayerButton label={titles.play} onClick={playStream} />
    }

    if (isPlaying) {
        button = <ProgressButton duration={duration} label={titles.playing} />
    }

    if (isLoading || isProcessing) {
        button = <ProcessingButton />
    }

    if (isStreaming) {
        button = (
            <RecordingButton
                analyser={recorder.analyser}
                maxRecordingTime={maxRecordingTime}
                isDisabled={recordingStopMode === RecordingMode.AUTO}
                onClick={async () => {
                    modal.open({
                        render: ({ onConfirm, onCancel }) => (
                            <PromptModalContent
                                caption={t('modals.completeStreaming.caption')}
                                onConfirm={async () => {
                                    onConfirm()
                                    await onRecordingEnd()
                                }}
                                onCancel={onCancel}
                            />
                        ),
                    })
                }}
                label={titles.recording}
            />
        )
    }

    return (
        <div style={props.style} className={css.container}>
            {button}
            {isPlaying && skippable && (
                <NavigationButton type="cancel" label="Skip" onClick={() => skipStream()} />
            )}
        </div>
    )
}
